// DXF panel-drawing exporter.
//
// One DXF file per cabinet (bundled into a ZIP for the project), OR a
// single combined DXF with all panels laid out in a grid. Each panel is
// a closed LWPOLYLINE rectangle on layer "PANEL"; the part name + cabinet
// id is a TEXT entity on layer "LABEL".
//
// Phase-4.1 work: drilling circles, edge-banding hatch fill, hinge cup
// pockets. Phase-4 ships outlines + labels — that's enough for any 2D
// CAM (AlphaCAM, BobCAD, Vectric, Mastercam) to import and post.

import type { Cabinet, CabinetPart } from "@/lib/types";
import { isDoorPart } from "@/lib/cabinets/roles";
import type { ExportArtifact, Postprocessor } from "./types";
import { makeZip } from "./zip";

const PANEL_GAP = 4; // inches between panels in combined layout

const dxfPanels: Postprocessor = {
  id: "dxf_panels",
  label: "Panel drawings (DXF)",
  description: "2D outline of every panel with cabinet/part labels. Loads in AlphaCAM, BobCAD, Vectric, Mastercam.",
  category: "drawing",
  extension: "dxf",
  options: [
    {
      key: "bundle",
      label: "Output",
      type: "select",
      default: "combined",
      choices: [
        { value: "combined",    label: "Single DXF (all panels in one drawing)" },
        { value: "per_cabinet", label: "One DXF per cabinet (ZIP)" },
      ],
    },
    {
      key: "include_doors",
      label: "Include doors / faces",
      type: "boolean",
      default: true,
    },
    {
      key: "units",
      label: "DXF units",
      type: "select",
      default: "in",
      choices: [
        { value: "in", label: "Inches" },
        { value: "mm", label: "Millimetres" },
      ],
    },
  ],
  generate(ctx, opts): ExportArtifact[] {
    const conv = opts.units === "mm" ? (n: number) => n * 25.4 : (n: number) => n;
    const unitCode = opts.units === "mm" ? 4 : 1; // DXF $INSUNITS

    const filterParts = (parts: CabinetPart[]) =>
      opts.include_doors ? parts : parts.filter((p) => !isDoorPart(p));

    if (opts.bundle === "per_cabinet") {
      const files: { name: string; content: string }[] = [];
      for (const { cabinet, parts } of ctx.parts) {
        const list = filterParts(parts);
        if (list.length === 0) continue;
        const dxf = renderDxf(cabinet, list, conv, unitCode);
        files.push({
          name: `${slug(ctx.project.name)}_cabinet_${cabinet._id}_${cabinet.classId}.dxf`,
          content: dxf,
        });
      }
      if (files.length === 0) {
        return [{
          filename: `${slug(ctx.project.name)}_panels_empty.txt`,
          content: "No panels to export.",
          mimeType: "text/plain",
        }];
      }
      const zip = makeZip(files.map((f) => ({ name: f.name, content: f.content })));
      return [{
        filename: `${slug(ctx.project.name)}_panels_dxf.zip`,
        content: zip,
        mimeType: "application/zip",
      }];
    }

    // Combined: lay out all panels in a flow-layout, grouped by cabinet,
    // each cabinet's row begins flush-left.
    const entities: string[] = [];
    let cursorY = 0;
    for (const { cabinet, parts } of ctx.parts) {
      const list = filterParts(parts);
      if (list.length === 0) continue;
      // Label at the top of this cabinet's row.
      writeText(entities, cabinet, cursorY + 1, conv);
      cursorY += 3; // labelspace
      let cursorX = 0;
      let rowMaxH = 0;
      for (const p of list) {
        for (let q = 0; q < (p.qty ?? 1); q++) {
          writePanelRect(entities, p, cursorX, cursorY, conv);
          writePartLabel(entities, p, cursorX, cursorY, conv);
          cursorX += conv(p.w) + conv(PANEL_GAP);
          rowMaxH = Math.max(rowMaxH, conv(p.h));
        }
      }
      cursorY += rowMaxH + conv(PANEL_GAP) * 2;
    }
    const dxf = wrap(entities, unitCode);
    return [{
      filename: `${slug(ctx.project.name)}_panels.dxf`,
      content: dxf,
      mimeType: "image/vnd.dxf",
    }];
  },
};

// ── DXF emission helpers ─────────────────────────────────────────

function renderDxf(cab: Cabinet, parts: CabinetPart[], conv: (n: number) => number, unitCode: number): string {
  const entities: string[] = [];
  writeText(entities, cab, 1, conv);
  let cursorX = 0;
  const baseY = 3;
  for (const p of parts) {
    for (let q = 0; q < (p.qty ?? 1); q++) {
      writePanelRect(entities, p, cursorX, baseY, conv);
      writePartLabel(entities, p, cursorX, baseY, conv);
      cursorX += conv(p.w) + conv(PANEL_GAP);
    }
  }
  return wrap(entities, unitCode);
}

function wrap(entityLines: string[], unitCode: number): string {
  const out: string[] = [];
  // HEADER
  out.push(
    "0", "SECTION",
    "2", "HEADER",
    "9", "$ACADVER",
    "1", "AC1009",
    "9", "$INSUNITS",
    "70", String(unitCode),
    "0", "ENDSEC",
  );
  // TABLES (minimal — just enough for layers)
  out.push(
    "0", "SECTION",
    "2", "TABLES",
    "0", "TABLE", "2", "LAYER", "70", "2",
    layerLine("PANEL", 7),
    layerLine("LABEL", 3),
    "0", "ENDTAB",
    "0", "ENDSEC",
  );
  // ENTITIES
  out.push("0", "SECTION", "2", "ENTITIES");
  out.push(...entityLines);
  out.push("0", "ENDSEC", "0", "EOF");
  return out.join("\n");
}

function layerLine(name: string, color: number): string {
  return [
    "0", "LAYER",
    "2", name,
    "70", "0",
    "62", String(color),
    "6", "CONTINUOUS",
  ].join("\n");
}

function writePanelRect(out: string[], p: CabinetPart, x0: number, y0: number, conv: (n: number) => number): void {
  const w = conv(p.w);
  const h = conv(p.h);
  out.push(
    "0", "LWPOLYLINE",
    "8", "PANEL",
    "90", "4",
    "70", "1",
    "10", x0.toFixed(4),         "20", y0.toFixed(4),
    "10", (x0 + w).toFixed(4),   "20", y0.toFixed(4),
    "10", (x0 + w).toFixed(4),   "20", (y0 + h).toFixed(4),
    "10", x0.toFixed(4),         "20", (y0 + h).toFixed(4),
  );
}

function writePartLabel(out: string[], p: CabinetPart, x0: number, y0: number, conv: (n: number) => number): void {
  out.push(
    "0", "TEXT",
    "8", "LABEL",
    "10", (x0 + 0.5).toFixed(4),
    "20", (y0 + conv(p.h) - 1).toFixed(4),
    "40", "0.5",   // text height
    "1",  truncate(`${p.name} ${p.w.toFixed(2)}x${p.h.toFixed(2)}`, 60),
  );
}

function writeText(out: string[], cab: Cabinet, y: number, _conv: (n: number) => number): void {
  out.push(
    "0", "TEXT",
    "8", "LABEL",
    "10", "0",
    "20", y.toFixed(4),
    "40", "1.0",
    "1",  truncate(`Cabinet #${cab._id} ${cab.classId} ${cab.width}x${cab.height}x${cab.depth}`, 60),
  );
}

function truncate(s: string, n: number): string {
  return s.length <= n ? s : s.slice(0, n - 1) + "…";
}

function slug(s: string): string {
  return s.replace(/[^\w]+/g, "_").replace(/^_+|_+$/g, "") || "project";
}

export default dxfPanels;
